import moment from 'moment';
import { SalesInvoiceBase } from '../abstract/sales-invoice-base.model';
import { QuotationItem } from './quotation-item.model';
import { QuotationTax } from './quotation-tax.model';
import { SalesInvoice } from '../invoice/sales-invoice.model';
import { QuotationTypeEnum } from 'src/app/@shared/enums/quotation-type.enum';
import { StatusEnum } from 'src/app/@shared/enums/status.enum';
import { tagAccents } from 'src/assets/themes/biifor/components/tag';

export class Quotation extends SalesInvoiceBase {

    static override CLASS_NAME: string = 'Quotation';

    private _acceptanceConditions!: string;
    private _acceptedQuotationId!: number;
    private _addendumEffectiveDate!: moment.Moment;
    private _addendumFastForward!: boolean;
    private _addendumNumber!: number;
    private _grossAmountBilled!: number;
    private _grossAmountBilledOrg!: number;
    private _grossAmountContract!: number;
    private _grossAmountContractOrg!: number;
    private _grossAmountDownpaid!: number;
    private _grossAmountDownpaidOrg!: number;
    private _grossAmountGap!: number;
    private _grossAmountGapOrg!: number;
    private _grossAmountIncidence!: number;
    private _grossAmountToBill!: number;
    private _grossAmountToBillOrg!: number;
    private _hasCommercialCap!: boolean;
    private _hasDownpaymentToSettle!: boolean;
    private _netAmountBilled!: number;
    private _netAmountBilledOrg!: number;
    private _netAmountContract!: number;
    private _netAmountContractOrg!: number;
    private _netAmountDownpaid!: number;
    private _netAmountDownpaidOrg!: number;
    private _netAmountGap!: number;
    private _netAmountGapOrg!: number;
    private _netAmountIncidence!: number;
    private _netAmountToBill!: number;
    private _netAmountToBillOrg!: number;
    private _previousVersionId!: number;
    private _taxAmountBilled!: number;
    private _taxAmountBilledOrg!: number;
    private _taxAmountContract!: number;
    private _taxAmountContractOrg!: number;
    private _taxAmountDownpaid!: number;
    private _taxAmountDownpaidOrg!: number;
    private _taxAmountGap!: number;
    private _taxAmountGapOrg!: number;
    private _taxAmountIncidence!: number;
    private _taxAmountToBill!: number;
    private _taxAmountToBillOrg!: number;
    private _validityDate!: moment.Moment;
    private _validationDate!: moment.Moment;
    private _versionNumber!: number;

    private _addendums!: Quotation[];
    private _contractItems!: QuotationItem[];
    private _items!: QuotationItem[];
    private _taxes!: QuotationTax[];

    private _invoices!: SalesInvoice[];

    constructor(data: any) {
        super(data);
        this._addendums = this._addendums || [];
        this._contractItems = this._contractItems || [];
        this._invoices = this._invoices || [];
        this._items = this._items || [];
        this._taxes = this._taxes || [];
    }

    public static override statusSeverity(status: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
        switch (true) {
            case (status === StatusEnum.Accepted):
                return undefined;
            case (status === StatusEnum.Billed):
            case (status === StatusEnum.DepositReceived):
            case (status === StatusEnum.PartiallyBilled):
                return 'success';
            case (status === StatusEnum.Cancelled):
            case (status === StatusEnum.Draft):
                return 'secondary';
            case (status === StatusEnum.Refused):
            case (status === StatusEnum.Replaced):
                return 'danger';
            case (status === StatusEnum.Sent):
                return undefined;
            case (status === StatusEnum.Validated):
                return 'warn';
            default:
                return undefined;
        }
    }

    public static override statusLabel(status: string): string {
        switch (true) {
            case (status === StatusEnum.Accepted):
                return 'Accepté';
            case (status === StatusEnum.Billed):
                return 'Facturé';
            case (status === StatusEnum.Cancelled):
                return 'Annulé';
            case (status === StatusEnum.Closed):
                return 'Clôturé';
            case (status === StatusEnum.Draft):
                return 'Brouillon';
            case (status === StatusEnum.DepositReceived):
                return 'Acompte';
            case (status === StatusEnum.PartiallyBilled):
                return 'Part. fact.';
            case (status === StatusEnum.Refused):
                return 'Refusé';
            case (status === StatusEnum.Replaced):
                return 'Remplacé';
            case (status === StatusEnum.Sent):
                return 'Envoyé';
            case (status === StatusEnum.Validated):
                return 'Finalisé';
            default:
                return '';
        }
    }

    public static override tagAccent(status: string): Object | undefined {
        switch (true) {
            case (status === StatusEnum.Accepted):
            case (status === StatusEnum.Billed):
            case (status === StatusEnum.Cancelled):
            case (status === StatusEnum.Refused):
                return tagAccents;
            default:
                return undefined;
        }
    }

    /**
     * Les statuts sont testés par ordre de priorité
     */
    public override get statusSeverity(): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
        switch (true) {
            case (this.hasStatus(StatusEnum.Billed)):
                return Quotation.statusSeverity(StatusEnum.Billed);
            case (this.hasStatus(StatusEnum.Closed)):
                return Quotation.statusSeverity(StatusEnum.Closed);
            case (this.hasStatus(StatusEnum.PartiallyBilled)):
                return Quotation.statusSeverity(StatusEnum.PartiallyBilled);
            case (this.hasStatus(StatusEnum.DepositReceived)):
                return Quotation.statusSeverity(StatusEnum.DepositReceived);
            case (this.hasStatus(StatusEnum.Accepted)):
                return Quotation.statusSeverity(StatusEnum.Accepted);
            case (this.hasStatus(StatusEnum.Refused)):
                return Quotation.statusSeverity(StatusEnum.Refused);
            case (this.hasStatus(StatusEnum.Replaced)):
                return Quotation.statusSeverity(StatusEnum.Replaced);
            case (this.hasStatus(StatusEnum.Cancelled)):
                return Quotation.statusSeverity(StatusEnum.Cancelled);
            case (this.hasStatus(StatusEnum.Sent)):
                return Quotation.statusSeverity(StatusEnum.Sent);
            case (this.hasStatus(StatusEnum.Validated)):
                return Quotation.statusSeverity(StatusEnum.Validated);
            case (this.hasStatus(StatusEnum.Draft)):
                return Quotation.statusSeverity(StatusEnum.Draft);
            default:
                return Quotation.statusSeverity('');
        }
    }

    /**
     * Les statuts sont testés par ordre de priorité
     */
    public override get statusLabel(): string {
        switch (true) {
            case (this.hasStatus(StatusEnum.Billed)):
                return Quotation.statusLabel(StatusEnum.Billed);
            case (this.hasStatus(StatusEnum.Closed)):
                return Quotation.statusLabel(StatusEnum.Closed);
            case (this.hasStatus(StatusEnum.PartiallyBilled)):
                return Quotation.statusLabel(StatusEnum.PartiallyBilled);
            case (this.hasStatus(StatusEnum.DepositReceived)):
                return Quotation.statusLabel(StatusEnum.DepositReceived);
            case (this.hasStatus(StatusEnum.Accepted)):
                return Quotation.statusLabel(StatusEnum.Accepted);
            case (this.hasStatus(StatusEnum.Refused)):
                return Quotation.statusLabel(StatusEnum.Refused);
            case (this.hasStatus(StatusEnum.Replaced)):
                return Quotation.statusLabel(StatusEnum.Replaced);
            case (this.hasStatus(StatusEnum.Cancelled)):
                return Quotation.statusLabel(StatusEnum.Cancelled);
            case (this.hasStatus(StatusEnum.Sent)):
                return Quotation.statusLabel(StatusEnum.Sent);
            case (this.hasStatus(StatusEnum.Validated)):
                return Quotation.statusLabel(StatusEnum.Validated);
            case (this.hasStatus(StatusEnum.Draft)):
                return Quotation.statusLabel(StatusEnum.Draft);
            default:
                return Quotation.statusLabel('');
        }
    }

    public override get tagAccent(): Object | undefined {
        switch (true) {
            case (this.hasStatus(StatusEnum.Billed)):
                return Quotation.tagAccent(StatusEnum.Billed);
            case (this.hasStatus(StatusEnum.Closed)):
                return Quotation.tagAccent(StatusEnum.Closed);
            case (this.hasStatus(StatusEnum.PartiallyBilled)):
                return Quotation.tagAccent(StatusEnum.PartiallyBilled);
            case (this.hasStatus(StatusEnum.DepositReceived)):
                return Quotation.tagAccent(StatusEnum.DepositReceived);
            case (this.hasStatus(StatusEnum.Accepted)):
                return Quotation.tagAccent(StatusEnum.Accepted);
            case (this.hasStatus(StatusEnum.Refused)):
                return Quotation.tagAccent(StatusEnum.Refused);
            case (this.hasStatus(StatusEnum.Replaced)):
                return Quotation.tagAccent(StatusEnum.Replaced);
            case (this.hasStatus(StatusEnum.Cancelled)):
                return Quotation.tagAccent(StatusEnum.Cancelled);
            case (this.hasStatus(StatusEnum.Sent)):
                return Quotation.tagAccent(StatusEnum.Sent);
            case (this.hasStatus(StatusEnum.Validated)):
                return Quotation.tagAccent(StatusEnum.Validated);
            case (this.hasStatus(StatusEnum.Draft)):
                return Quotation.tagAccent(StatusEnum.Draft);
            default:
                return undefined;
        }
    }

    public get acceptanceConditions(): string {
        return this._acceptanceConditions;
    }

    public set acceptanceConditions(value: any) {
        this._setValue('_acceptanceConditions', value, 'string');
    }

    public get acceptedQuotationId(): number {
        return this._acceptedQuotationId;
    }

    public set acceptedQuotationId(value: any) {
        this._setValue('_acceptedQuotationId', value, 'integer');
    }

    public get addendumEffectiveDate(): moment.Moment {
        return this._addendumEffectiveDate;
    }

    public set addendumEffectiveDate(value: any) {
        this._setValue('_addendumEffectiveDate', value, 'date');
    }

    /**
     * Acceptation automatique de l'avenant (#1001) : à la validation, le serveur
     * enchaîne envoi puis acceptation, sans passage par le client.
     *
     * La valeur est **posée sur le document** à sa création (résolution de la cascade
     * paramétrage des ventes → fiche client), reste éditable tant que l'avenant est
     * en brouillon, et fait foi ensuite — c'est elle, la trace. Sans objet sur un
     * devis, qui garde sa validation explicite.
     */
    public get addendumFastForward(): boolean {
        return this._addendumFastForward;
    }

    public set addendumFastForward(value: any) {
        this._setValue('_addendumFastForward', value, 'boolean');
    }

    public get addendumNumber(): number {
        return this._addendumNumber;
    }

    public set addendumNumber(value: any) {
        this._setValue('_addendumNumber', value, 'integer');
    }

    public get addendums(): Quotation[] {
        return this._addendums;
    }

    public set addendums(value: any) {
        this._setValue('_addendums', value, 'Quotation');
    }

    public get allocations(): any[] {
        return [];
    }

    public set allocations(_: any) { }

    /**
     * Lignes du **contrat en vigueur** : celles du devis racine et celles de ses
     * avenants effectifs, chacune portant son `sourceDocument`. Servi par l'expand
     * `*` du détail, vide en liste et sur un avenant.
     *
     * ⚠️ À ne pas confondre avec `items`, qui reste les lignes **du document lu** :
     * dès qu'un avenant est effectif, tout suivi d'avancement doit lire celles-ci,
     * sans quoi l'entête compte des lignes que le détail ignore.
     */
    public get contractItems(): QuotationItem[] {
        return this._contractItems;
    }

    public set contractItems(value: any) {
        this._setValue('_contractItems', value, 'QuotationItem');
    }

    public get grossAmountBilled(): number {
        return this._grossAmountBilled;
    }

    public set grossAmountBilled(value: any) {
        this._setValue('_grossAmountBilled', value, 'float');
    }

    public get grossAmountBilledOrg(): number {
        return this._grossAmountBilledOrg;
    }

    public set grossAmountBilledOrg(value: any) {
        this._setValue('_grossAmountBilledOrg', value, 'float');
    }

    public get grossAmountContract(): number {
        return this._grossAmountContract;
    }

    public set grossAmountContract(value: any) {
        this._setValue('_grossAmountContract', value, 'float');
    }

    public get grossAmountContractOrg(): number {
        return this._grossAmountContractOrg;
    }

    public set grossAmountContractOrg(value: any) {
        this._setValue('_grossAmountContractOrg', value, 'float');
    }

    public override get grossAmountDisp(): number {
        return this.grossAmount;
    }

    public get grossAmountDownpaid(): number {
        return this._grossAmountDownpaid;
    }

    public set grossAmountDownpaid(value: any) {
        this._setValue('_grossAmountDownpaid', value, 'float');
    }

    public get grossAmountDownpaidOrg(): number {
        return this._grossAmountDownpaidOrg;
    }

    public set grossAmountDownpaidOrg(value: any) {
        this._setValue('_grossAmountDownpaidOrg', value, 'float');
    }

    public get grossAmountGap(): number {
        return this._grossAmountGap;
    }

    public set grossAmountGap(value: any) {
        this._setValue('_grossAmountGap', value, 'float');
    }

    public get grossAmountGapOrg(): number {
        return this._grossAmountGapOrg;
    }

    public set grossAmountGapOrg(value: any) {
        this._setValue('_grossAmountGapOrg', value, 'float');
    }

    public get grossAmountIncidence(): number {
        return this._grossAmountIncidence;
    }

    public set grossAmountIncidence(value: any) {
        this._setValue('_grossAmountIncidence', value, 'float');
    }

    public get grossAmountToBill(): number {
        return this._grossAmountToBill;
    }

    public set grossAmountToBill(value: any) {
        this._setValue('_grossAmountToBill', value, 'float');
    }

    public get grossAmountToBillOrg(): number {
        return this._grossAmountToBillOrg;
    }

    public set grossAmountToBillOrg(value: any) {
        this._setValue('_grossAmountToBillOrg', value, 'float');
    }

    /**
     * Vrai quand un plafond posé par une **décision commerciale** traîne sur le contrat
     * en vigueur — les plafonds posés par un avenant ne comptent pas (#1006).
     *
     * ⚠️ C'est la seule condition d'affichage valable pour « Rouvrir » : sur un devis
     * amendé, les lignes de la racine portent un plafond posé par l'avenant, et le front
     * n'a pas les moyens de faire la différence. Servi sur la fiche seulement (absent des
     * listes).
     */
    public get hasCommercialCap(): boolean {
        return this._hasCommercialCap;
    }

    public set hasCommercialCap(value: any) {
        this._setValue('_hasCommercialCap', value, 'boolean');
    }

    /**
     * Vrai quand le périmètre du contrat est couvert — reste à facturer nul — mais qu'un
     * **acompte reste à consommer** : une facture définitive est encore à émettre, celle
     * qui constate le produit et solde le compte d'avances client (#999).
     *
     * ⚠️ C'est la seule façon de distinguer un devis réellement soldé d'un devis en
     * attente de cette facture : les deux affichent un reste à facturer à zéro. Ne pas
     * déduire la condition des `items` — elle porte sur le résiduel d'acompte ligne à
     * ligne du contrat, avenants compris. Servi sur la fiche seulement (absent des
     * listes).
     */
    public get hasDownpaymentToSettle(): boolean {
        return this._hasDownpaymentToSettle;
    }

    public set hasDownpaymentToSettle(value: any) {
        this._setValue('_hasDownpaymentToSettle', value, 'boolean');
    }

    public get invoices(): SalesInvoice[] {
        return this._invoices;
    }

    public set invoices(value: any) {
        this._setValue('_invoices', value, 'SalesInvoice');
    }

    /**
     * Vrai pour un document d'avenant (`type.code === 'addendum'`).
     * ⚠️ `type` n'est pas toujours hydraté (les listes restreignent le `$select`) :
     * l'accès reste optionnel, un `type` absent vaut « devis ».
     */
    public get isAddendum(): boolean {
        return this.type?.code === QuotationTypeEnum.Addendum;
    }

    public get items(): QuotationItem[] {
        return this._items;
    }

    public set items(value: any) {
        this._setValue('_items', value, 'QuotationItem');
    }

    public get netAmountBilled(): number {
        return this._netAmountBilled;
    }

    public set netAmountBilled(value: any) {
        this._setValue('_netAmountBilled', value, 'float');
    }

    public get netAmountBilledOrg(): number {
        return this._netAmountBilledOrg;
    }

    public set netAmountBilledOrg(value: any) {
        this._setValue('_netAmountBilledOrg', value, 'float');
    }

    public get netAmountContract(): number {
        return this._netAmountContract;
    }

    public set netAmountContract(value: any) {
        this._setValue('_netAmountContract', value, 'float');
    }

    public get netAmountContractOrg(): number {
        return this._netAmountContractOrg;
    }

    public set netAmountContractOrg(value: any) {
        this._setValue('_netAmountContractOrg', value, 'float');
    }

    public override get netAmountDisp(): number {
        return this.netAmount;
    }

    public get netAmountDownpaid(): number {
        return this._netAmountDownpaid;
    }

    public set netAmountDownpaid(value: any) {
        this._setValue('_netAmountDownpaid', value, 'float');
    }

    public get netAmountDownpaidOrg(): number {
        return this._netAmountDownpaidOrg;
    }

    public set netAmountDownpaidOrg(value: any) {
        this._setValue('_netAmountDownpaidOrg', value, 'float');
    }

    public get netAmountGap(): number {
        return this._netAmountGap;
    }

    public set netAmountGap(value: any) {
        this._setValue('_netAmountGap', value, 'float');
    }

    public get netAmountGapOrg(): number {
        return this._netAmountGapOrg;
    }

    public set netAmountGapOrg(value: any) {
        this._setValue('_netAmountGapOrg', value, 'float');
    }

    public get netAmountIncidence(): number {
        return this._netAmountIncidence;
    }

    public set netAmountIncidence(value: any) {
        this._setValue('_netAmountIncidence', value, 'float');
    }

    public get netAmountToBill(): number {
        return this._netAmountToBill;
    }

    public set netAmountToBill(value: any) {
        this._setValue('_netAmountToBill', value, 'float');
    }

    public get netAmountToBillOrg(): number {
        return this._netAmountToBillOrg;
    }

    public set netAmountToBillOrg(value: any) {
        this._setValue('_netAmountToBillOrg', value, 'float');
    }

    public get previousVersionId(): number {
        return this._previousVersionId;
    }

    public set previousVersionId(value: any) {
        this._setValue('_previousVersionId', value, 'integer');
    }

    public get taxAmountBilled(): number {
        return this._taxAmountBilled;
    }

    public set taxAmountBilled(value: any) {
        this._setValue('_taxAmountBilled', value, 'float');
    }

    public get taxAmountBilledOrg(): number {
        return this._taxAmountBilledOrg;
    }

    public set taxAmountBilledOrg(value: any) {
        this._setValue('_taxAmountBilledOrg', value, 'float');
    }

    public get taxAmountContract(): number {
        return this._taxAmountContract;
    }

    public set taxAmountContract(value: any) {
        this._setValue('_taxAmountContract', value, 'float');
    }

    public get taxAmountContractOrg(): number {
        return this._taxAmountContractOrg;
    }

    public set taxAmountContractOrg(value: any) {
        this._setValue('_taxAmountContractOrg', value, 'float');
    }

    public get taxAmountDownpaid(): number {
        return this._taxAmountDownpaid;
    }

    public set taxAmountDownpaid(value: any) {
        this._setValue('_taxAmountDownpaid', value, 'float');
    }

    public get taxAmountDownpaidOrg(): number {
        return this._taxAmountDownpaidOrg;
    }

    public set taxAmountDownpaidOrg(value: any) {
        this._setValue('_taxAmountDownpaidOrg', value, 'float');
    }

    public get taxAmountGap(): number {
        return this._taxAmountGap;
    }

    public set taxAmountGap(value: any) {
        this._setValue('_taxAmountGap', value, 'float');
    }

    public get taxAmountGapOrg(): number {
        return this._taxAmountGapOrg;
    }

    public set taxAmountGapOrg(value: any) {
        this._setValue('_taxAmountGapOrg', value, 'float');
    }

    public get taxAmountIncidence(): number {
        return this._taxAmountIncidence;
    }

    public set taxAmountIncidence(value: any) {
        this._setValue('_taxAmountIncidence', value, 'float');
    }

    public get taxAmountToBill(): number {
        return this._taxAmountToBill;
    }

    public set taxAmountToBill(value: any) {
        this._setValue('_taxAmountToBill', value, 'float');
    }

    public get taxAmountToBillOrg(): number {
        return this._taxAmountToBillOrg;
    }

    public set taxAmountToBillOrg(value: any) {
        this._setValue('_taxAmountToBillOrg', value, 'float');
    }

    public get taxes(): QuotationTax[] {
        return this._taxes;
    }

    public set taxes(value: any) {
        this._setValue('_taxes', value, 'QuotationTax');
    }

    public get validityDate(): moment.Moment {
        return this._validityDate;
    }

    public set validityDate(value: any) {
        this._setValue('_validityDate', value, 'date');
    }

    public get validationDate(): moment.Moment {
        return this._validationDate;
    }

    public set validationDate(value: any) {
        this._setValue('_validationDate', value, 'date');
    }

    /**
     * Référence affichable du document : la référence, suffixée du numéro d'avenant
     * puis du numéro de version — `DEV-001`, `DEV-001-A1`, `DEV-001-A1-v2`.
     *
     * ⚠️ Sur un avenant, `reference` porte la référence de la **racine** (un avenant
     * ne consomme pas de numéro de devis) : c'est ce getter, et lui seul, qui pose
     * le `-A<n>`. Tant que l'avenant est en brouillon, la référence n'est pas encore
     * attribuée — d'où le repli sur `referenceTmp`, qui en donne l'aperçu.
     *
     * ⚠️ Ne pas l'appliquer aux entrées de `addendums` : le serveur y sert **déjà**
     * la référence suffixée dans leur champ `reference`, et ce getter la suffixerait
     * une seconde fois.
     */
    public get versionedReference(): string {
        const base = this.reference || this.referenceTmp;
        if (!base) return '';
        let reference = base;
        if (this.isAddendum && !!this.addendumNumber) {
            reference += '-A' + this.addendumNumber;
        }
        if (this.versionNumber > 1) {
            reference += '-v' + this.versionNumber;
        }
        return reference;
    }

    public get versionNumber(): number {
        return this._versionNumber;
    }

    public set versionNumber(value: any) {
        this._setValue('_versionNumber', value, 'integer');
    }

}
